export const SUPPORTED_LANGS = ['de', 'en'] as const;
export type SupportedLang = (typeof SUPPORTED_LANGS)[number];

export const LANGUAGE_STORAGE_KEY = 'smartrecon-lang';
const LANGUAGE_COOKIE_KEY = LANGUAGE_STORAGE_KEY;
const ONE_YEAR_IN_SECONDS = 60 * 60 * 24 * 365;

export function isSupportedLang(lang: string | null | undefined): lang is SupportedLang {
  return !!lang && (SUPPORTED_LANGS as readonly string[]).includes(lang);
}

export function resolveLanguageFromCookie(cookieHeader: string | null | undefined): SupportedLang | null {
  if (!cookieHeader) {
    return null;
  }

  const cookieEntry = cookieHeader
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${LANGUAGE_COOKIE_KEY}=`));

  if (!cookieEntry) {
    return null;
  }

  const cookieValue = decodeURIComponent(cookieEntry.slice(LANGUAGE_COOKIE_KEY.length + 1));
  return isSupportedLang(cookieValue) ? cookieValue : null;
}

export function resolveLanguageFromAcceptLanguage(headerValue: string | null | undefined): SupportedLang | null {
  if (!headerValue) {
    return null;
  }

  for (const part of headerValue.split(',')) {
    const candidate = normalizeLanguageToken(part);

    if (isSupportedLang(candidate)) {
      return candidate;
    }
  }

  return null;
}

export function resolveLanguageFromBrowser(browserLanguage: string | null | undefined): SupportedLang | null {
  const candidate = normalizeLanguageToken(browserLanguage);
  return isSupportedLang(candidate) ? candidate : null;
}

export function buildLanguageCookie(lang: SupportedLang): string {
  return `${LANGUAGE_COOKIE_KEY}=${encodeURIComponent(lang)}; Path=/; Max-Age=${ONE_YEAR_IN_SECONDS}; SameSite=Lax`;
}

function normalizeLanguageToken(lang: string | null | undefined): string | null {
  if (!lang) {
    return null;
  }

  const [languagePart] = lang.trim().toLowerCase().split(';', 1);
  const [baseLanguage] = languagePart.split('-', 1);

  return baseLanguage || null;
}
